import { motion } from "framer-motion";
import SerifGlow from "./SerifGlow";

const ASSET = "https://qclay.design/lovable/bags";
const TEXT_COLOR = "#545454";

const HEADLINE_TYPE = {
  fontFamily: "'Fredoka', 'Inter Tight', sans-serif",
  fontWeight: 600,
  fontSize: "87.999px",
  lineHeight: "88px",
  letterSpacing: "-2.2px",
  color: TEXT_COLOR,
} as const;

export default function Hero() {
  return (
    <section
      className="relative w-full overflow-hidden"
      style={{
        background: "#EEEAE3",
        fontFamily: "'Inter Tight', sans-serif",
        minHeight: "100vh",
      }}
    >


      {/* Heading block */}
      <div
        className="absolute"
        style={{ top: 32, left: 40, maxWidth: 500, zIndex: 10 }}
      >
        <motion.div
          initial={{ opacity: 0, filter: "blur(14px)" }}
          animate={{ opacity: 1, filter: "blur(0px)" }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
          style={HEADLINE_TYPE}
        >
          Handmade
        </motion.div>
        <motion.div
          initial={{ opacity: 0, filter: "blur(14px)" }}
          animate={{ opacity: 1, filter: "blur(0px)" }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.28 }}
          style={HEADLINE_TYPE}
        >
          cuddle with
        </motion.div>
        <div
          className="flex"
          style={{ alignItems: "baseline", gap: 12, marginTop: -2 }}
        >
          <SerifGlow
            word="you"
            fontSize={94.969}
            lineHeight={93.413}
            letterSpacing={-3.799}
            strokeWidth={20.55}
            delay={0.5}
          />
          <motion.span
            initial={{ opacity: 0, filter: "blur(12px)" }}
            animate={{ opacity: 1, filter: "blur(0px)" }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.78 }}
            style={HEADLINE_TYPE}
          >
            forever
          </motion.span>
        </div>
      </div>

      {/* Woman model */}
      <motion.img
        src="https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/1354ed68c266f1a07cf8ed36dd23fd31.png"
        alt="Model with a shoulder bag"
        initial={{ opacity: 0, y: 80, x: 120 }}
        animate={{ opacity: 1, y: 0, x: 120 }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          marginLeft: "auto",
          marginRight: "auto",
          height: "90vh",
          width: "auto",
          objectFit: "contain",
          objectPosition: "bottom center",
          zIndex: 6,
        }}
      />

      {/* Sticks flourish */}
      <motion.img
        src={`${ASSET}/sticks.svg`}
        alt=""
        style={{
          position: "absolute",
          top: 24,
          left: "calc(50% + 40px)",
          zIndex: 7,
          width: 32,
          transformOrigin: "bottom center",
        }}
        initial={{ opacity: 0, scale: 0, rotate: -180 }}
        animate={{
          opacity: 1,
          scale: [0, 1.4, 0.85, 1.15, 0.95, 1.05, 1],
          rotate: [-180, -20, 25, -15, 10, -5, 0],
        }}
        transition={{
          opacity: { duration: 0.3, delay: 0.85, ease: "easeOut" },
          scale: { duration: 0.95, delay: 0.85, ease: [0.34, 1.56, 0.64, 1] },
          rotate: { duration: 0.95, delay: 0.85, ease: [0.34, 1.56, 0.64, 1] },
        }}
      />
      <motion.img
        src={`${ASSET}/sticks.svg`}
        alt=""
        style={{
          position: "absolute",
          top: 24,
          left: "calc(50% + 40px)",
          zIndex: 7,
          width: 32,
          transformOrigin: "bottom center",
          opacity: 0,
        }}
        animate={{ opacity: [0, 0], y: [0, -6, 0] }}
        transition={{
          y: { duration: 3.2, delay: 2, repeat: Infinity, ease: "easeInOut" },
        }}
      />

      {/* Cart icon - static, left of model */}
      <img
        src="https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/cab4ef9042f6e79beee397363ec1aac7.png"
        alt=""
        style={{
          position: "absolute",
          top: "calc(50% - 100px)",
          left: "calc(50% - 560px)",
          zIndex: 5,
          width: 400,
          height: 400,
        }}
      />

      {/* Snap polaroid center */}
      <motion.img
        src="https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/521b748f8eef54e4e182f77ffcdbcdea.png"
        alt=""
        style={{
          position: "absolute",
          top: "65%",
          left: "calc(50% + 160px)",
          zIndex: 8,
          width: 200,
          transformPerspective: 500,
          transformOrigin: "top center",
        }}
        initial={{ rotateX: -100, scaleY: 0.1, opacity: 0, rotate: -6 }}
        animate={{
          rotateX: [-100, -60, -15, 4, -1, 0],
          scaleY: [0.1, 0.35, 0.8, 1.03, 0.99, 1],
          opacity: [0, 0.35, 0.85, 1, 1, 1],
          rotate: [-6, 2, -10, 4, -8, -6],
        }}
        transition={{
          rotateX: {
            duration: 0.65,
            delay: 1.1,
            ease: [0.22, 1, 0.36, 1],
            times: [0, 0.2, 0.55, 0.78, 0.9, 1],
          },
          scaleY: {
            duration: 0.65,
            delay: 1.1,
            ease: [0.22, 1, 0.36, 1],
            times: [0, 0.2, 0.55, 0.78, 0.9, 1],
          },
          opacity: {
            duration: 0.65,
            delay: 1.1,
            ease: [0.22, 1, 0.36, 1],
            times: [0, 0.2, 0.55, 0.78, 0.9, 1],
          },
          rotate: {
            duration: 5,
            delay: 1.8,
            repeat: Infinity,
            ease: "easeInOut",
          },
        }}
      />

      {/* Gift card left of model */}
      <motion.img
        src="https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/0ea31e334ac970f024cadb12fd7c8632.png"
        alt=""
        style={{
          position: "absolute",
          bottom: "22%",
          left: "calc(50% - 170px)",
          zIndex: 9,
          width: 150,
          transformPerspective: 600,
          transformOrigin: "top center",
        }}
        initial={{ rotateX: -90, scaleY: 0.12, opacity: 0, rotate: -3 }}
        animate={{
          rotateX: [-90, -50, -10, 3, 0],
          scaleY: [0.12, 0.5, 0.9, 1.02, 1],
          opacity: [0, 0.4, 0.9, 1, 1],
          rotate: -3,
        }}
        transition={{
          duration: 0.6,
          delay: 1.2,
          ease: [0.22, 1, 0.36, 1],
          times: [0, 0.25, 0.65, 0.85, 1],
        }}
      />

      {/* "elegance" sticker */}
      <div
        style={{
          position: "absolute",
          bottom: "calc(16% + 40px)",
          left: "calc(50% - 100px)",
          zIndex: 10,
          transform: "rotate(6.206deg)",
        }}
      >
        <SerifGlow
          word="handmade"
          fontSize={32}
          lineHeight={31}
          letterSpacing={-1.2}
          strokeWidth={10.27}
          delay={1.35}
          italic
        />
      </div>

      {/* Text heart sticker */}
      <motion.img
        src={`${ASSET}/text-heart.png`}
        alt=""
        style={{
          position: "absolute",
          top: "57%",
          left: "calc(50% + 150px)",
          zIndex: 7,
          width: 110,
        }}
        initial={{ opacity: 0, scale: 0.5, rotate: 18 }}
        animate={{ opacity: 1, scale: 1, rotate: 4 }}
        transition={{ duration: 0.5, delay: 1.3, ease: [0.34, 1.56, 0.64, 1] }}
      />

      {/* Arrow */}
      <motion.img
        src={`${ASSET}/arrow.svg`}
        alt=""
        style={{
          position: "absolute",
          top: "38%",
          right: 60,
          zIndex: 7,
          width: 90,
        }}
        initial={{ opacity: 0, x: 24, rotate: 20 }}
        animate={{ opacity: 0.8, x: [0, -5, 0], rotate: 0 }}
        transition={{
          opacity: { duration: 0.55, delay: 1.4, ease: "easeOut" },
          rotate: { duration: 0.55, delay: 1.4, ease: "easeOut" },
          x: {
            duration: 2.2,
            delay: 2,
            repeat: Infinity,
            ease: "easeInOut",
          },
        }}
      />

      {/* LOVE BAG right column */}
      <motion.div
        initial={{ opacity: 0, x: 24, filter: "blur(6px)" }}
        animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
        transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
        style={{
          position: "absolute",
          top: "22%",
          right: 32,
          zIndex: 10,
          maxWidth: 210,
        }}
      >
        <div className="flex items-center" style={{ gap: 6 }}>
          <span
            style={{
              fontFamily: "'Inter Tight', sans-serif",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "2.5px",
              color: TEXT_COLOR,
            }}
          >
            LOVE PLUSH
          </span>
          <img src={`${ASSET}/heart.svg`} alt="" style={{ width: 13, height: 13 }} />
        </div>
        <p
          style={{
            fontFamily: "'Inter Tight', sans-serif",
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 1.7,
            color: TEXT_COLOR,
            textAlign: "justify",
            marginTop: 10,
          }}
        >
          Hand-knitted stitch by stitch and made to snuggle from bedtime to playtime, each little friend carries not just softness, but tiny stories of its own
        </p>
      </motion.div>

      {/* Large watermark (01) */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
        style={{
          position: "absolute",
          bottom: 20,
          right: 32,
          zIndex: 4,
          fontFamily: "'Instrument Serif', serif",
          fontWeight: 400,
          fontSize: "87.999px",
          lineHeight: "80px",
          letterSpacing: "-3.52px",
          color: "rgba(84,84,84,0.18)",
        }}
      >
        (01)
      </motion.div>

      {/* Bottom-left polaroid thumbnails */}
      <div
        className="flex"
        style={{
          position: "absolute",
          bottom: 24,
          left: 32,
          zIndex: 10,
          gap: 20,
          alignItems: "flex-end",
        }}
      >
        <PolaroidThumb tag="(02)" img="https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/90e3040299e9c2f6aa558ed5c9e7d291.png" delay={1.65} rotate={-2} />
        <PolaroidThumb tag="(03)" img="https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/8a2d4ee735a13c473521479a240e520a.png" delay={1.8} rotate={1.5} />
      </div>
    </section>
  );
}

interface PolaroidThumbProps {
  tag: string;
  img: string;
  delay: number;
  rotate: number;
}

function PolaroidThumb({ tag, img, delay, rotate }: PolaroidThumbProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: "easeOut" }}
      style={{
        transform: `rotate(${rotate}deg)`,
        position: "relative",
      }}
    >
      {/* Tag label */}
      <div
        style={{
          position: "absolute",
          top: -32,
          right: 14,
          zIndex: 3,
          fontFamily: "'Instrument Serif', serif",
          fontSize: 18,
          fontWeight: 400,
          color: "rgba(84,84,84,0.7)",
          pointerEvents: "none",
        }}
      >
        {tag}
      </div>
      <div
        className="polaroid-inner"
        style={{
          width: 170,
          height: 210,
          position: "relative",
          filter: "drop-shadow(2px 6px 14px rgba(0,0,0,0.10))",
          transition: "all 0.25s ease",
          cursor: "pointer",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-4px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        <img
          src={img.startsWith("http") ? img : `${ASSET}/${img}`}
          alt=""
          style={{
            position: "absolute",
            top: "14%",
            left: "14%",
            width: "72%",
            height: "62%",
            objectFit: "contain",
            zIndex: 1,
          }}
        />
        <img
          src={`${ASSET}/snap-bare.png`}
          alt=""
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            zIndex: 2,
            pointerEvents: "none",
          }}
        />
      </div>
    </motion.div>
  );
}